import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Home, Menu as MenuIcon, Close, Collections } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { scrollToSection } from './smoothScroll';
import styles from './Menu.module.scss';

const Menu = ({ currentPage }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('main');

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }

    const sections = ['main', 'about', 'skills', 'projects', 'contact'];
    const observers = [];

    const getMostVisibleSection = (entries) => {
      let maxVisibility = 0;
      let mostVisibleSection = null;

      entries.forEach(entry => {
        const visiblePx = entry.intersectionRect.height;
        if (visiblePx > maxVisibility) {
          maxVisibility = visiblePx;
          mostVisibleSection = entry.target.id;
        }
      });

      return mostVisibleSection;
    };

    let visibleSections = new Set();

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          visibleSections.add(entry.target.id);
        } else {
          visibleSections.delete(entry.target.id);
        }
      });

      if (visibleSections.size > 0) {
        const entries = Array.from(document.querySelectorAll('div[data-page]'))
          .filter(el => visibleSections.has(el.id))
          .map(el => ({
            target: el,
            intersectionRect: el.getBoundingClientRect()
          }));

        const mostVisible = getMostVisibleSection(entries);
        if (mostVisible) {
          setActiveSection(mostVisible);
        }
      }
    };

    sections.forEach(sectionId => {
      const element = document.getElementById(sectionId);
      if (element) {
        const observer = new IntersectionObserver(observerCallback, {
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
          rootMargin: '-10% 0px -10% 0px'
        });
        observer.observe(element);
        observers.push(observer);
      } else {
        console.warn(`Element with id "${sectionId}" not found`);
      }
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = (e) => {
    const targetId = e.currentTarget.getAttribute('href').slice(1);
    setActiveSection(targetId);
    scrollToSection(e);
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  const isGalleryActive = currentPage === 'gallery' || location.pathname === '/gallery';

  const menuItems = (
    <ul className={styles.menu__list}>
      <li className={styles.menu__item}>
        <Link
          to="/#main"
          className={`${styles.menu__link} ${activeSection === 'main' && location.pathname === '/' ? styles.menu__link_active : ''}`}
          onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              setActiveSection('main');
              const el = document.getElementById('main');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }
            if (isMobileMenuOpen) setIsMobileMenuOpen(false);
          }}
        >
          <Home />
        </Link>
      </li>
      <li className={styles.menu__item}>
        <Link
          to="/#about"
          className={`${styles.menu__link} ${activeSection === 'about' && location.pathname === '/' ? styles.menu__link_active : ''}`}
          onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              handleLinkClick(e);
            }
          }}
        >
          {t('menu.about')}
        </Link>
      </li>
      <li className={styles.menu__item}>
        <Link
          to="/#skills"
          className={`${styles.menu__link} ${activeSection === 'skills' && location.pathname === '/' ? styles.menu__link_active : ''}`}
          onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              handleLinkClick(e);
            }
          }}
        >
          {t('menu.skills')}
        </Link>
      </li>
      <li className={styles.menu__item}>
        <Link
          to="/#projects"
          className={`${styles.menu__link} ${activeSection === 'projects' && location.pathname === '/' ? styles.menu__link_active : ''}`}
          onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              handleLinkClick(e);
            }
          }}
        >
          {t('menu.projects')}
        </Link>
      </li>
      <li className={styles.menu__item}>
        <Link
          to="/#contact"
          className={`${styles.menu__link} ${activeSection === 'contact' && location.pathname === '/' ? styles.menu__link_active : ''}`}
          onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              handleLinkClick(e);
            }
          }}
        >
          {t('menu.contact')}
        </Link>
      </li>
      |
      <li className={styles.menu__item}>
        <Link
          to="/cv"
          className={`${styles.menu__link} ${location.pathname === '/cv' ? styles.menu__link_active : ''}`}
          onClick={() => {
            if (isMobileMenuOpen) setIsMobileMenuOpen(false);
          }}
        >
          {t('menu.cv')}
        </Link>
      </li>
      <li className={styles.menu__item}>
        <Link
          to="/gallery"
          className={styles.menu__galleryButton}
          onClick={() => {
            if (isMobileMenuOpen) setIsMobileMenuOpen(false);
          }}
          aria-label="Open gallery"
        >
          <Collections />
          <span>{t('menu.gallery')}</span>
        </Link>
      </li>
      <li className={styles.menu__item} style={{ display: 'flex', gap: '10px' }}>
        <button onClick={() => changeLanguage('pl')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <img src="/pl.png" alt="PL" style={{ width: '30px', height: 'auto', opacity: i18n.language === 'pl' ? 1 : 0.5 }} />
        </button>
        <button onClick={() => changeLanguage('en')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <img src="/eng.png" alt="EN" style={{ width: '30px', height: 'auto', opacity: i18n.language === 'en' ? 1 : 0.5 }} />
        </button>
      </li>
    </ul>
  );

  if (isGalleryActive) {
    return (
      <Link
        to="/"
        className={styles.menu__closeButton}
        aria-label="Close gallery"
      >
        <Close />
      </Link>
    );
  }

  return (
    <>
      <nav className={`${styles.menu} ${isScrolled ? styles.menu__scrolled : ''}`}>
        <div className={styles.menu__mobile}>
          <button
            className={styles.menu__hamburger}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <Close /> : <MenuIcon />}
          </button>
        </div>

        <div className={`${styles.menu__desktop} ${isMobileMenuOpen ? styles.menu__mobile_open : ''}`}>
          {menuItems}
          {isMobileMenuOpen && (
            <button
              className={styles.menu__close}
              onClick={toggleMobileMenu}
              aria-label="Close menu"
            >
              <Close />
            </button>
          )}
        </div>
      </nav>
    </>
  );
};

Menu.propTypes = {
  currentPage: PropTypes.string
};

Menu.defaultProps = {
  currentPage: 'home'
};

export default Menu;
